/**
 * Cache Service for Admin Panel
 * Handles localStorage operations for caching API data.
 *
 * Hardening (see PR notes):
 *  - Entries expire after CACHE_TTL_MS (24h). Expired entries are treated as
 *    a miss so the UI refetches instead of showing indefinitely-stale data.
 *  - Cache keys are namespaced by the active backend BASE_URL so data fetched
 *    from one environment (local / dev / prod) never shows up against another.
 *  - clearAllCache() wipes every admin cache entry regardless of backend; it
 *    is called on logout so a new session always starts clean.
 */

import { BASE_URL } from '../api-config';

const CACHE_PREFIX = 'admin_panel_cache_';

// Time-to-live for cached entries: 24 hours.
export const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Sanitize the backend BASE_URL into a short, key-safe namespace so that
 * switching environments (e.g. localhost:3030 -> dev API gateway) does not
 * read another environment's cached data.
 */
const getBackendNamespace = () =>
  String(BASE_URL || 'unknown').replace(/[^a-zA-Z0-9]/g, '_');

/**
 * Build the fully-qualified localStorage key for a logical cache key,
 * namespaced to the current backend.
 * @param {string} key - Logical cache key (e.g. 'users')
 * @returns {string}
 */
const buildCacheKey = (key) => `${CACHE_PREFIX}${getBackendNamespace()}_${key}`;

/**
 * Get cached data from localStorage.
 *
 * Returns null on a miss, on corrupted data, or when the entry has expired
 * (older than CACHE_TTL_MS) unless `ignoreTTL` is set. The expired-entry path
 * is used by the normal read; the `ignoreTTL` escape hatch lets the data layer
 * fall back to stale data when the API is unreachable (resilience), which it
 * does explicitly rather than silently.
 *
 * @param {string} key - Cache key (without prefix)
 * @param {{ ignoreTTL?: boolean }} [options]
 * @returns {any|null} - The cache envelope { data, timestamp, version } or null
 */
export const getCachedData = (key, options = {}) => {
  const { ignoreTTL = false } = options;
  try {
    const cacheKey = buildCacheKey(key);
    const cachedItem = localStorage.getItem(cacheKey);

    if (!cachedItem) {
      return null;
    }

    const parsedData = JSON.parse(cachedItem);

    // Treat entries without a usable timestamp as expired so legacy/corrupted
    // envelopes don't live forever.
    const timestamp = typeof parsedData?.timestamp === 'number' ? parsedData.timestamp : 0;
    const age = Date.now() - timestamp;

    if (!ignoreTTL && age > CACHE_TTL_MS) {
      console.log(`Cache expired for ${key} (age ${Math.round(age / 1000)}s > TTL)`);
      return null;
    }

    return parsedData;
  } catch (error) {
    console.error(`Error reading cache for ${key}:`, error);
    // Clear corrupted cache entry
    clearCachedData(key);
    return null;
  }
};

/**
 * Store data in localStorage cache.
 * @param {string} key - Cache key (without prefix)
 * @param {any} data - Data to cache
 * @returns {boolean} - Success status
 */
export const setCachedData = (key, data) => {
  try {
    const cacheKey = buildCacheKey(key);
    const cacheData = {
      data,
      timestamp: Date.now(),
      version: '1.0'
    };

    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    return true;
  } catch (error) {
    console.error(`Error caching data for ${key}:`, error);
    return false;
  }
};

/**
 * Remove specific cached data (for the current backend namespace).
 * @param {string} key - Cache key (without prefix)
 * @returns {boolean} - Success status
 */
export const clearCachedData = (key) => {
  try {
    const cacheKey = buildCacheKey(key);
    localStorage.removeItem(cacheKey);
    console.log(`Cache cleared for ${key}`);
    return true;
  } catch (error) {
    console.error(`Error clearing cache for ${key}:`, error);
    return false;
  }
};

/**
 * Clear ALL admin panel cached data across every backend namespace.
 * Called on login and logout so sessions never inherit stale data.
 * @returns {boolean} - Success status
 */
export const clearAllCache = () => {
  try {
    const keys = Object.keys(localStorage);
    const adminCacheKeys = keys.filter(key => key.startsWith(CACHE_PREFIX));

    adminCacheKeys.forEach(key => {
      localStorage.removeItem(key);
    });

    console.log(`Cleared ${adminCacheKeys.length} cache entries`);
    return true;
  } catch (error) {
    console.error('Error clearing all cache:', error);
    return false;
  }
};

/**
 * Check if (non-expired) data exists in cache.
 * @param {string} key - Cache key (without prefix)
 * @returns {boolean}
 */
export const hasCachedData = (key) => {
  return getCachedData(key) !== null;
};

/**
 * Get cache info for debugging.
 * @returns {Object} - Cache statistics
 */
export const getCacheInfo = () => {
  try {
    const keys = Object.keys(localStorage);
    const adminCacheKeys = keys.filter(key => key.startsWith(CACHE_PREFIX));

    const cacheInfo = {
      totalEntries: adminCacheKeys.length,
      entries: adminCacheKeys.map(key => {
        const data = localStorage.getItem(key);
        try {
          const parsed = JSON.parse(data);
          return {
            key: key.replace(CACHE_PREFIX, ''),
            timestamp: parsed.timestamp,
            ageMs: parsed.timestamp ? Date.now() - parsed.timestamp : null,
            size: data.length
          };
        } catch {
          return {
            key: key.replace(CACHE_PREFIX, ''),
            corrupted: true
          };
        }
      })
    };

    return cacheInfo;
  } catch (error) {
    console.error('Error getting cache info:', error);
    return { totalEntries: 0, entries: [] };
  }
};
